import {
  HTTP_STATUS_SERVER_ERROR_MIN,
  HTTP_STATUS_TOO_MANY_REQUESTS,
  HTTP_STATUS_UNAUTHORIZED,
  getErrorStatus,
  isApiError,
  isCanceledError,
} from '~/common/utils/api.util';
import dialog from '~/common/utils/dialog.util';
import { APP_MODE, ViteAppMode } from '~/common/utils/env.util.ts';
import { isRecord } from '~/common/utils/type-guard.util.ts';
import { signOutApi } from '~/features/auth/api/auth.api';
import { buildSignInPathWithReturnTo } from '~/features/auth/utils/auth.util';

import type { ReactNode } from 'react';

/**
 * 서버 사유를 못 얻었을 때의 기본 문구.
 * @description **"새로고침" 을 지시하지 않는다.** 종전 문구가 새로고침을 안내해, SMS 인증 게이트에서
 *  에러를 만난 사용자가 안내대로 새로고침하면 인증 완료 상태와 전 입력이 소실돼 SMS 재발송이
 *  강제됐다(실비용 + BE 쿨다운 재대기 · deep-verify H4).
 */
const DEFAULT_FALLBACK_MESSAGE = '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
const SERVER_ERROR_MESSAGE = '서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
const TOO_MANY_REQUESTS_MESSAGE = '요청이 너무 잦습니다. 잠시 후 다시 시도해주세요.';
const SESSION_EXPIRED_MESSAGE = '로그인이 만료되었습니다. 다시 로그인해주세요.';
const SESSION_EXPIRED_TITLE = '다시 로그인해주세요';

interface ErrorHandlerOptions {
  /** 에러 다이얼로그 제목 (어떤 작업 중 오류인지) */
  title: string;
  /** 서버 메시지가 없을 때 표시할 기본 메시지 */
  fallbackMessage?: string;
  /** 다이얼로그 표시 여부 (기본값: true) */
  showDialog?: boolean;
  /** 401 에러 시 로그인 페이지 리다이렉트 비활성화 (기본값: false) */
  disableAuthCheck?: boolean;
}

/**
 * API 에러 응답에서 사용자에게 보여줄 사유 추출.
 * @description Supabase(PostgrestError·AuthError)와 일반 Error 가 섞여 들어온다 — message 만 사용자에게 보인다.
 *  ① CUSTOM: `{"type":1, "message":"..."}`
 *  ② VALIDATOR: `{"type":0, "errors":[{errorCode,status,fieldName,message}, ...]}`
 *  ③ **`type` 부재** (v1 다수 경로):
 *     - 400 검증: `{"status":400, "message":[{errorCode,status,fieldName,message}, ...]}`
 *     - 401 인증: `{"status":401, "message":"invalid access token"}`
 *  ①② 만 파싱하던 종전 구현은 ③ 을 통째로 놓쳐 전 폼에서 BE 원문 사유가 fallback 문구로
 *  덮였다(deep-verify L7 / 티켓 `validator-error-shape-align`). ①② 분기는 그대로 두고 ③ 을 폴백으로 더한다.
 *  판정은 타입이 아니라 이 함수의 런타임 가드가 전담한다 — 응답 union 을 narrow 하는 소비처는 없다.
 */
function extractApiErrorContent(error: unknown): ReactNode {
  if (!isRecord(error)) return null;

  // Supabase 는 `message`, PostgREST 는 `message` + `details` 를 준다. 사용자에게는 message 만 보인다.
  const { message } = error as { message?: unknown };
  return typeof message === 'string' && message.trim() ? message : null;
}

/**
 * 상태코드로 사유를 구분한다.
 * @description 인증번호 오류(400 · BE 원문)와 서버 장애(5xx)·쿨다운(429)이 같은 문구로 수렴하면
 *  사용자가 "내 입력이 틀렸는지 / 서버가 죽었는지" 판단할 수 없다(deep-verify H4).
 */
function resolveStatusFallback(status: number | undefined, fallbackMessage: string): string {
  if (typeof status !== 'number') return fallbackMessage;
  if (status === HTTP_STATUS_UNAUTHORIZED) return SESSION_EXPIRED_MESSAGE;
  if (status === HTTP_STATUS_TOO_MANY_REQUESTS) return TOO_MANY_REQUESTS_MESSAGE;
  if (status >= HTTP_STATUS_SERVER_ERROR_MIN) return SERVER_ERROR_MESSAGE;
  return fallbackMessage;
}

/**
 * 401 Unauthorized 처리 — 만료 안내 후 returnTo 를 실어 로그인 페이지로 이동.
 * @description redux 사용자 정보를 따로 비우지 않는 이유: 이동을 `window.location.href` 로 하므로
 *  문서가 통째로 재적재돼 store 가 초기 상태로 다시 만들어진다(정리 dispatch 는 무의미한 중복).
 *  SPA 내비게이션으로 축출하는 경로는 `useAuth.handleSessionExpired` 가 따로 담당하며 그쪽은 store 를 비운다.
 */
async function handleUnauthorized(): Promise<void> {
  // 세션 정리는 Supabase 가 담당한다(쿠키 토큰을 앱이 직접 들고 있지 않다).
  await signOutApi();
  const target = buildSignInPathWithReturnTo(window.location.pathname, window.location.search);

  // 무통보 축출 방지 — 왜 튕겼는지 알리고 확인을 받은 뒤에 이동한다.
  await dialog.message({ title: SESSION_EXPIRED_TITLE, content: SESSION_EXPIRED_MESSAGE });
  window.location.href = target;
}

/**
 * 전역 에러 핸들러
 * @description API 에러, 네트워크 에러, 로컬 에러를 일관되게 처리
 * @param error - 에러 객체
 * @param options - 핸들러 옵션
 * @param options.title - 에러 다이얼로그 제목 (필수)
 * @param options.fallbackMessage - 서버 메시지가 없을 때 표시할 기본 메시지
 * @param options.showDialog - 다이얼로그 표시 여부 (기본값: true)
 */
export async function globalErrorHandler(error: unknown, options: ErrorHandlerOptions) {
  const {
    title,
    fallbackMessage = DEFAULT_FALLBACK_MESSAGE,
    showDialog = true,
    disableAuthCheck = false,
  } = options;

  // 라우트 이탈로 취소된 요청 — 호출 문맥이 이미 사라졌으므로 다음 화면에 다이얼로그를 띄우지 않는다.
  if (isCanceledError(error)) return;

  const dialogTitle = title;
  let dialogContent: ReactNode = fallbackMessage;

  if (isApiError(error)) {
    const status = getErrorStatus(error);

    // 401 Unauthorized — 세션 정리 후 로그인으로 축출 (disableAuthCheck 가 false 일 때만)
    if (status === HTTP_STATUS_UNAUTHORIZED && !disableAuthCheck) {
      await handleUnauthorized();
      return;
    }

    // 서버가 준 사유를 우선 쓰고, 없으면 상태코드별 기본 문구로 접는다.
    const serverContent = extractApiErrorContent(error);
    dialogContent = serverContent || resolveStatusFallback(status, fallbackMessage);

    if (APP_MODE === ViteAppMode.DEVELOPMENT) {
      // eslint-disable-next-line no-console
      console.warn('[API Error]', error);
    }
  } else if (APP_MODE === ViteAppMode.DEVELOPMENT) {
    // 로컬 에러 (비즈니스 로직 에러 등) - 호출부 title + fallback 메시지
    // eslint-disable-next-line no-console
    console.warn('[Local Error]', error);
  }

  // 종전과 동일하게 표시 후 즉시 반환한다(다이얼로그 닫힘을 기다리지 않음).
  if (showDialog) {
    dialog.message({ title: dialogTitle, content: dialogContent });
  }
}
